/**
 * Mother's Day gift tags — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mothersDayGiftTagsPack: ToilePack = {
    slug: 'mothers-day-gift-tags',
    category: 'season',
    name: "Mother's Day gift tags",
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'floral', label: 'Floral tags', subject: 'a sheet of six rectangular tags with a punched hole at the top, each wreathed in small peonies and printed "Happy Mother\'s Day"'},
        {key: 'plant', label: 'Plant pot tags', subject: 'a sheet of round tags for a potted plant reading "Thanks for Helping Me Grow", a small sprouting seedling drawn under the words'},
        {key: 'tofrom', label: 'To and from', subject: 'a sheet of eight luggage-shaped tags with a heart in one corner and "To: Mom" and "From:" printed above a blank line'},
    ],
    styles: [
        {key: 'kraft', label: 'Kraft paper', style: 'tags in white and dark ink as if printed on kraft paper, hand-stamped look, simple line motifs', palette: 'kraft brown with white and rose-pink ink', framing: 'Flat on the sheet, evenly spaced'},
        {key: 'flat', label: 'Flat color', style: 'flat vector illustration, bold clean shapes, smooth gradient-free fills, crisp lettering', palette: 'peony pink, sage green, cream and charcoal, four colors', framing: 'Full page on a solid cream ground with an even margin'},
        {key: 'lineart', label: 'Line art', style: 'fine black line art, uniform thin stroke, closed shapes, no fills, simple hand lettering', palette: 'black ink only', framing: 'Drawn on plain white paper with a clean margin'},
    ],
    directions: ['Ribbon', 'Jam jar', 'Gold foil'],
    directionHint: 'try: Ribbon',
    seo: {
        title: "Mother's Day Gift Tags: Floral, Plant Pot, To and From",
        metaDescription: "Printable Mother's Day gift tags to cut out and tie on: floral, plant pot and to-and-from tags, in three styles.",
        h1: "Mother's Day gift tags: floral, plant pot, to and from",
        tagline: 'Nine sheets of cut-out tags with the greeting printed and a hole for string, in three styles.',
        intro: "Printable tags to tie on a pot of flowers, a jar of jam or a wrapped Mother's Day gift. Floral tags are wreathed in peonies and read Happy Mother's Day, plant pot tags say Thanks for Helping Me Grow above a seedling, and to-and-from tags have To: Mom and From lines. Kraft paper, flat color or line art. Type a different greeting or gift, and the same tag layout carries it.",
        useCases: [
            'a tag on a pot of flowers',
            'a label on a jar of jam',
            'a tag on a wrapped gift',
            'a card insert for a classroom gift',
        ],
        faq: [
            {q: 'How are the tags laid out?', a: 'The tags sit in rows with a margin around the sheet and space between them to cut.'},
            {q: 'What does the kraft style look like?', a: 'The tags are drawn in white and dark ink on kraft brown paper, with a hand-stamped look.'},
            {q: 'Can the greeting be changed?', a: 'Yes. Type a different greeting or gift, such as a jar of jam, and the same layout carries it.'},
        ],
    },
}
